import { getParser } from "@/lib/file-parser";
import { supabase } from "@/lib/supabase";

async function attachFileToProject({
  fileId,
  projectId,
}: {
  fileId: string;
  projectId: string;
}): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from("project_files")
    .upsert(
      { project_id: projectId, file_id: fileId },
      { onConflict: "project_id,file_id", ignoreDuplicates: true }
    );

  if (error) {
    console.error("[attachFileToProject] attach error:", error);
    return { ok: false, error: error.message };
  }

  return { ok: true };
}

async function indexFileForRag({
  fileId,
  filename,
}: {
  fileId: string;
  filename: string;
}): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.functions.invoke("woods-knowledge-upload", {
    body: {
      fileId,
      parser: getParser(undefined, filename),
      embedding_provider: "voyage",
      embedding_model: "voyage-context-3",
    },
  });

  if (error) {
    console.error("[indexFileForRag] invoke error:", error);
    return { ok: false, error: error.message };
  }

  return { ok: true };
}

/**
 * Whether this file is already attached to the project AND successfully
 * indexed (survives page reload). Checking `project_files` alone isn't
 * enough — the attach can succeed while the woods-knowledge-upload embedding
 * step fails or is still running, and we don't want the button to claim
 * "added" for a file that isn't actually searchable yet.
 */
export async function isFileInProject({
  fileId,
  projectId,
}: {
  fileId: string;
  projectId: string;
}): Promise<boolean> {
  const { data, error } = await supabase
    .from("project_files")
    .select("file_id, files(processing_status)")
    .eq("project_id", projectId)
    .eq("file_id", fileId)
    .maybeSingle();

  if (error) {
    console.error("[isFileInProject] lookup error:", error);
    return false;
  }

  if (!data) return false;

  // project_files.file_id -> files.id is many-to-one, so PostgREST embeds a
  // single object at runtime — but without generated Database types the
  // client can't infer that cardinality, so normalize defensively here.
  type FileStatus = { processing_status: string | null };
  const embedded = data.files as unknown as FileStatus | FileStatus[] | null;
  const status = Array.isArray(embedded) ? embedded[0]?.processing_status : embedded?.processing_status;
  return status !== "FAILED";
}

/**
 * Attaches an already-saved `files` row to a project and kicks off
 * chunking/embedding so it becomes retrievable by the knowledgeBase RAG tool.
 *
 * A generated file's chat may already have silently upserted a `project_files`
 * row (see /api/files/[fileId]/save) when the chat belongs to a project, but
 * that path never triggers woods-knowledge-upload — so the file sits in
 * project_files unindexed until this is called.
 */
export async function addGeneratedFileToProject({
  fileId,
  filename,
  projectId,
}: {
  fileId: string;
  filename: string;
  projectId: string;
}): Promise<{ ok: boolean; error?: string }> {
  const attached = await attachFileToProject({ fileId, projectId });
  if (!attached.ok) return attached;

  return indexFileForRag({ fileId, filename });
}

/**
 * For chat-rendered files that only exist as inline text content (e.g. the
 * present_files/create_file file tool, for any non-HTML output) — no
 * `files` row exists yet, so this uploads the content to storage first, then
 * attaches + indexes it.
 */
export async function uploadPresentedFileToProject({
  filename,
  content,
  mimeType,
  projectId,
  userId,
}: {
  filename: string;
  content: string;
  mimeType: string;
  projectId: string;
  userId: string;
}): Promise<{ ok: boolean; error?: string }> {
  const file = new File([content], filename, { type: mimeType });
  const ext = filename.includes(".") ? filename.slice(filename.lastIndexOf(".")) : "";
  const storagePath = `${userId}/${crypto.randomUUID()}${ext}`;

  const { error: storageError } = await supabase.storage
    .from("files")
    .upload(storagePath, file, { contentType: mimeType, upsert: false });
  if (storageError) {
    console.error("[uploadPresentedFileToProject] storage error:", storageError);
    return { ok: false, error: storageError.message };
  }

  const { data: fileRecord, error: fileError } = await supabase
    .from("files")
    .insert({
      user_id: userId,
      file_path: storagePath,
      name: filename,
      size: file.size,
      tokens: 0,
      type: mimeType,
      description: "",
      sharing: "private",
    })
    .select("id")
    .single();
  if (fileError || !fileRecord) {
    console.error("[uploadPresentedFileToProject] insert error:", fileError);
    return { ok: false, error: fileError?.message ?? "Insert failed" };
  }

  return addGeneratedFileToProject({ fileId: fileRecord.id, filename, projectId });
}
